import { RefObject } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { consumeReturnTarget, peekReturnTarget } from './drawerFocusReturn';
import { HOTKEY_SCOPES } from './scopes';

export interface DrawerEscapeOptions {
  /** Disable the binding without unmounting the caller. Default `true`. */
  enabled?: boolean;
}

/**
 * Context-aware Escape for the drawer:
 *  - If focus is inside THIS drawer's tablist AND a return target exists →
 *    return focus to the table (drawer stays open). preventDefault + stopPropagation.
 *  - Else → onClose() (normal drawer close).
 *
 * Replaces the plain `useEscapeHotkey(onClose)` at the Drawer call-site so a
 * SINGLE handler decides return-vs-close. react-hotkeys-hook v5 fires EVERY
 * enabled handler bound to the pressed key in active scopes with no ordering
 * guarantee, so two competing `overlay` Esc handlers (one returning, one
 * closing) could both fire — the branch must live in one handler.
 *
 * Only the topmost drawer is `escapeKey`-armed by `Drawers`, so exactly one
 * `useDrawerEscape` is enabled at a time — no parent/child double-fire. The
 * exit window is covered too: a closed drawer exiting the shared
 * AnimatePresence keeps its cached `escapeKey`/`open` props, so the Drawer
 * call-site also gates `enabled` on `useIsPresent()` — the exiting instance
 * disarms the moment the promoted top drawer takes ownership.
 */
export function useDrawerEscape(
  onClose: () => void,
  drawerRef: RefObject<HTMLElement | null>, // ref to the scroll container ([data-testid="drawer-content"])
  opts: DrawerEscapeOptions = {}
): void {
  const { enabled = true } = opts;

  useHotkeys(
    'escape',
    (event: KeyboardEvent) => {
      const drawerEl = drawerRef.current;
      const active = document.activeElement;
      // Scope to the drawer's PRIMARY tablist only — the FIRST [role="tablist"]
      // in document order, which is exactly the one the ArrowRight entry path
      // focuses (useTableArrowNav.ts targets `topDrawer.querySelector(
      // '[role="tablist"]')`). Using `active.closest('[role="tablist"]')` matched
      // ANY ancestor tablist, so Esc fired from a nested (e.g. POE) inner tablist
      // would return focus to the table and leave the drawer open. Anchoring to
      // the primary tablist keeps Esc-return symmetric with ArrowRight-enter:
      // nested-tablist Esc falls through to the normal onClose() below.
      const primaryTablist =
        drawerEl?.querySelector('[role="tablist"]') ?? null;
      const inThisDrawersTablist =
        !!drawerEl &&
        active instanceof HTMLElement &&
        drawerEl.contains(active) &&
        !!primaryTablist &&
        primaryTablist.contains(active);

      if (inThisDrawersTablist && peekReturnTarget()) {
        event.preventDefault();
        event.stopPropagation();
        // Back to the table; drawer stays open. Do not let focus scroll the
        // hidden background page/table and clip its sticky header.
        consumeReturnTarget()?.focus({ preventScroll: true });
        return;
      }

      onClose();
    },
    {
      enabled,
      enableOnFormTags: ['input', 'textarea', 'select'],
      scopes: [HOTKEY_SCOPES.overlay],
    },
    [onClose, drawerRef]
  );
}
